import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // ALWAYS default to dark - KetoLab is a dark-first app
    const saved = localStorage.getItem('ketolab-theme');
    // Only use 'light' if explicitly saved as 'light', otherwise DARK
    if (saved === 'light') {
      return 'light';
    }
    // Force dark as default
    return 'dark';
  });

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('ketolab-theme', theme);
    
    // Apply theme to document and body
    const root = document.documentElement;
    const body = document.body;
    
    if (theme === 'light') {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
      body.classList.add('light-theme');
      body.classList.remove('dark-theme');
    } else {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
      body.classList.add('dark-theme');
      body.classList.remove('light-theme');
      // Force dark background on body
      body.style.backgroundColor = '#0a0a0a';
    }
  }, [theme]);

  // On mount, ensure dark theme is applied
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    // If no theme set, force dark
    if (!localStorage.getItem('ketolab-theme')) {
      root.classList.add('dark-theme');
      body.classList.add('dark-theme');
      body.style.backgroundColor = '#0a0a0a';
    }
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const setDarkTheme = () => setTheme('dark');
  const setLightTheme = () => setTheme('light');

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setDarkTheme,
    setLightTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
